import type { SqlDriver } from "./types";

/**
 * The device replica's schema, versioned with `PRAGMA user_version`. Append a new array to change
 * it - never edit a shipped one: a phone that is months behind must migrate step by step.
 *
 * Tables mirror the server's sync entities (docs/SYNC.md). Money is TEXT holding the server's
 * exact decimal string; sums are done with BigInt (decimal.ts), never by SQLite as floating
 * point. `balance` on accounts is the server-computed figure.
 */
export const MIGRATIONS: readonly (readonly string[])[] = [
  // v1 - the replica
  [
    `CREATE TABLE meta (key TEXT PRIMARY KEY, value TEXT NOT NULL)`,

    `CREATE TABLE accounts (
       id TEXT PRIMARY KEY,
       name TEXT NOT NULL,
       account_type TEXT NOT NULL,
       account_class TEXT NOT NULL,
       currency TEXT NOT NULL,
       opening_balance TEXT NOT NULL,
       opening_date TEXT NOT NULL,
       institution TEXT,
       notes TEXT,
       is_active INTEGER NOT NULL,
       deleted_at TEXT,
       version INTEGER NOT NULL,
       balance TEXT NOT NULL,
       created_at TEXT NOT NULL,
       updated_at TEXT NOT NULL
     )`,

    `CREATE TABLE categories (
       id TEXT PRIMARY KEY,
       name TEXT NOT NULL,
       category_type TEXT NOT NULL,
       parent_id TEXT,
       icon TEXT,
       is_active INTEGER NOT NULL,
       is_default INTEGER NOT NULL,
       sort_order INTEGER NOT NULL,
       deleted_at TEXT,
       version INTEGER NOT NULL,
       updated_at TEXT NOT NULL
     )`,

    `CREATE TABLE merchants (
       id TEXT PRIMARY KEY,
       name TEXT NOT NULL,
       normalised_name TEXT NOT NULL,
       default_category_id TEXT,
       deleted_at TEXT,
       version INTEGER NOT NULL,
       updated_at TEXT NOT NULL
     )`,

    `CREATE TABLE budgets (
       id TEXT PRIMARY KEY,
       category_id TEXT NOT NULL,
       period_type TEXT NOT NULL,
       period_start TEXT NOT NULL,
       amount TEXT NOT NULL,
       currency TEXT NOT NULL,
       version INTEGER NOT NULL,
       updated_at TEXT NOT NULL
     )`,

    `CREATE TABLE transactions (
       id TEXT PRIMARY KEY,
       transaction_type TEXT NOT NULL,
       transaction_date TEXT NOT NULL,
       amount TEXT NOT NULL,
       currency TEXT NOT NULL,
       account_id TEXT NOT NULL,
       counter_account_id TEXT,
       category_id TEXT,
       adjustment_direction TEXT,
       merchant_id TEXT,
       description TEXT NOT NULL,
       reference TEXT,
       notes TEXT,
       original_transaction_id TEXT,
       import_batch_id TEXT,
       voided_at TEXT,
       void_reason TEXT,
       version INTEGER NOT NULL,
       created_at TEXT NOT NULL,
       updated_at TEXT NOT NULL,
       postings TEXT
     )`,
    `CREATE INDEX ix_transactions_date ON transactions (transaction_date DESC, created_at DESC, id DESC)`,
    `CREATE INDEX ix_transactions_account ON transactions (account_id)`,
    `CREATE INDEX ix_transactions_counter ON transactions (counter_account_id) WHERE counter_account_id IS NOT NULL`,
    `CREATE INDEX ix_transactions_category ON transactions (category_id) WHERE category_id IS NOT NULL`,
    `CREATE INDEX ix_transactions_original ON transactions (original_transaction_id) WHERE original_transaction_id IS NOT NULL`,
  ],

  // v2 - writing while offline: the outbox (the sync queue) and pending merchant names.
  [
    // One row per change the user made on this device, in the order they made it. `status`:
    //   pending  - waiting to be sent
    //   sending  - in a request that has not been answered yet (reset to pending after a crash;
    //              replaying it is safe, the server remembers each op_id)
    //   acked    - the server applied it; kept only until the pull cursor has caught up with the
    //              server's `acked_head`, so the provisional balance never counts it twice
    //   conflict / rejected - the server refused it; kept, with the reason, for the user to review
    // `effects` is this operation's exact effect on each account's balance ({accountId: "-350.00"}),
    // which is what the provisional ("pending") balance is made of.
    `CREATE TABLE outbox (
       seq INTEGER PRIMARY KEY AUTOINCREMENT,
       op_id TEXT NOT NULL UNIQUE,
       action TEXT NOT NULL,
       entity_id TEXT NOT NULL,
       base_version INTEGER,
       payload TEXT NOT NULL,
       effects TEXT NOT NULL,
       status TEXT NOT NULL,
       acked_head INTEGER,
       error_code TEXT,
       error_message TEXT,
       error_fields TEXT,
       server TEXT,
       created_at TEXT NOT NULL
     )`,
    `CREATE INDEX ix_outbox_entity ON outbox (entity_id)`,
    `CREATE INDEX ix_outbox_status ON outbox (status, seq)`,
    // A merchant typed while offline has no server row (and so no id) until it syncs.
    `ALTER TABLE transactions ADD COLUMN merchant_name TEXT`,
  ],
];

export const SCHEMA_VERSION = MIGRATIONS.length;

export async function schemaVersion(driver: SqlDriver): Promise<number> {
  const rows = await driver.query<{ user_version: number }>("PRAGMA user_version");
  return Number(rows[0]?.user_version ?? 0);
}

/** Brings a database up to the current schema, one atomic step per version. */
export async function migrate(driver: SqlDriver): Promise<void> {
  const current = await schemaVersion(driver);
  if (current > SCHEMA_VERSION) {
    // Written by a newer app version (e.g. this tab is an old cached build). Never downgrade.
    throw new Error(`local database is newer (v${current}) than this app understands (v${SCHEMA_VERSION})`);
  }
  for (let version = current; version < SCHEMA_VERSION; version++) {
    await driver.batch([
      ...(MIGRATIONS[version] ?? []).map((sql) => ({ sql })),
      { sql: `PRAGMA user_version = ${version + 1}` },
    ]);
  }
}
